import { AdminModuleKey, ProjectWorkspaceModuleKey, SubModuleKey, User } from '../../types';
import { hasModuleAccess, hasSubPermission } from '../../utils/modulePermissions';

export interface ProjectWorkspaceNavItem {
  path: ProjectWorkspaceModuleKey;
  labelEn: string;
  labelEs: string;
  moduleKey: AdminModuleKey | AdminModuleKey[];
  requiresManage?: boolean;
  adminOnly?: boolean;
  requiresProjectFinancialView?: boolean;
  requiredSubPermissions?: SubModuleKey[];
}

export interface ProjectWorkspaceNavGroup {
  key: 'summary' | 'execution' | 'coordination' | 'planning' | 'costs' | 'administration';
  labelEn: string;
  labelEs: string;
  items: ProjectWorkspaceNavItem[];
}

export const PROJECT_WORKSPACE_NAV_GROUPS: ProjectWorkspaceNavGroup[] = [
  {
    key: 'summary',
    labelEn: 'Summary',
    labelEs: 'Resumen',
    items: [
      { path: 'overview', labelEn: 'Overview', labelEs: 'Resumen', moduleKey: 'projects' },
    ],
  },
  {
    key: 'execution',
    labelEn: 'Daily execution',
    labelEs: 'Ejecución diaria',
    items: [
      { path: 'production', labelEn: 'Production', labelEs: 'Producción', moduleKey: 'reports' },
      { path: 'control', labelEn: 'Control', labelEs: 'Control', moduleKey: 'control' },
      // Overview, Field Reports and Free Reports as tabs (see ProjectReportsHub).
      { path: 'reports', labelEn: 'Reports', labelEs: 'Reportes', moduleKey: ['reports', 'field_logs', 'free_reports'] },
      { path: 'timesheets', labelEn: 'Timesheets', labelEs: 'Hojas de tiempo', moduleKey: 'timesheets' },
    ],
  },
  {
    key: 'coordination',
    labelEn: 'Project coordination',
    labelEs: 'Coordinación del proyecto',
    items: [
      { path: 'documents', labelEn: 'Documents', labelEs: 'Documentos', moduleKey: 'documents' },
      { path: 'drawings', labelEn: 'Drawings', labelEs: 'Planos', moduleKey: 'drawings' },
      { path: 'rfis', labelEn: 'RFIs', labelEs: 'RFIs', moduleKey: 'rfis' },
    ],
  },
  {
    key: 'planning',
    labelEn: 'Planning & resources',
    labelEs: 'Planificación y recursos',
    items: [
      { path: 'schedule', labelEn: 'Schedule', labelEs: 'Programa', moduleKey: 'schedule' },
      {
        path: 'scope-areas',
        labelEn: 'Scope & Areas',
        labelEs: 'Alcance y áreas',
        moduleKey: 'reports',
        requiresManage: true,
        requiredSubPermissions: ['reports.setup_structure', 'reports.setup_targets'],
      },
      // Project Workforce is scoped by project membership. Global People access
      // is intentionally not required; edit actions still enforce the separate
      // projects.manage_staffing capability in the page and API.
      { path: 'workforce', labelEn: 'Workforce', labelEs: 'Personal', moduleKey: 'projects' },
      { path: 'materials', labelEn: 'Materials', labelEs: 'Materiales', moduleKey: 'warehouse' },
    ],
  },
  {
    key: 'costs',
    labelEn: 'Costs & results',
    labelEs: 'Costos y resultados',
    items: [
      {
        path: 'labor-budgets',
        labelEn: 'Labor Budgets',
        labelEs: 'Presupuestos',
        moduleKey: 'projects',
        adminOnly: true,
        requiresProjectFinancialView: true,
      },
    ],
  },
  {
    key: 'administration',
    labelEn: 'Administration',
    labelEs: 'Administración',
    items: [
      {
        path: 'setup',
        labelEn: 'Project Settings',
        labelEs: 'Ajustes del proyecto',
        moduleKey: 'projects',
        requiresManage: true,
      },
    ],
  },
];

export const CONFIGURABLE_PROJECT_MODULES: ProjectWorkspaceModuleKey[] = PROJECT_WORKSPACE_NAV_GROUPS
  .flatMap((group) => group.items.map((item) => item.path))
  .filter((path) => path !== 'overview' && path !== 'setup');

export function isProjectWorkspaceModuleEnabled(
  path: ProjectWorkspaceModuleKey,
  disabledModules: ProjectWorkspaceModuleKey[] = [],
): boolean {
  return path === 'overview' || path === 'setup' || !disabledModules.includes(path);
}

export function canViewProjectWorkspaceItem(user: User | null, item: ProjectWorkspaceNavItem): boolean {
  if (!user || (item.adminOnly && user.role !== 'admin')) return false;
  if (
    item.requiresProjectFinancialView
    && !user.is_developer
    && !user.is_operator
    && !user.is_owner_admin
    && !hasSubPermission(user, 'projects.financials.view')
  ) return false;
  if (
    item.requiredSubPermissions
    && !item.requiredSubPermissions.some((permission) => hasSubPermission(user, permission))
  ) return false;

  const keys = Array.isArray(item.moduleKey) ? item.moduleKey : [item.moduleKey];
  const level = item.requiresManage ? 'manage' : 'view';
  return keys.some((key) => hasModuleAccess(user, key, level));
}

export function getVisibleProjectWorkspaceGroups(
  user: User | null,
  disabledModules: ProjectWorkspaceModuleKey[] = [],
): ProjectWorkspaceNavGroup[] {
  return PROJECT_WORKSPACE_NAV_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => (
        isProjectWorkspaceModuleEnabled(item.path, disabledModules)
        && canViewProjectWorkspaceItem(user, item)
      )),
    }))
    .filter((group) => group.items.length > 0);
}

export function getVisibleProjectWorkspaceItems(
  user: User | null,
  disabledModules: ProjectWorkspaceModuleKey[] = [],
): ProjectWorkspaceNavItem[] {
  return getVisibleProjectWorkspaceGroups(user, disabledModules).flatMap((group) => group.items);
}

export function getProjectWorkspaceItem(path: string): ProjectWorkspaceNavItem | null {
  return PROJECT_WORKSPACE_NAV_GROUPS
    .flatMap((group) => group.items)
    .find((item) => item.path === path) ?? null;
}

export function getDefaultProjectWorkspacePath(
  user: User | null,
  disabledModules: ProjectWorkspaceModuleKey[] = [],
): string | null {
  return getVisibleProjectWorkspaceItems(user, disabledModules)[0]?.path ?? null;
}
